import React, { useMemo, useState } from "react";
import { BookmarkCheck, Check } from "lucide-react";
import {
  LibraryPage,
  FilterTabs,
  EmptyState,
  Poster,
  Chips,
  LikeButton,
  ConfirmRemove,
  applyTypeFilter,
  splitGenres,
} from "./LibraryLayout";

export default function MyListView({
  items,
  isLiked,
  onToggleLike,
  onMarkWatched,
  onRemove,
  onBack,
  toast,
  overlay,
}) {
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null); // title of the card being updated

  const visible = useMemo(
    () => applyTypeFilter(items, filter),
    [items, filter],
  );

  const run = async (item, fn) => {
    setBusy(item.title);
    try {
      await fn(item);
    } finally {
      setBusy(null);
    }
  };

  const count = items.length;
  const subtitle =
    count === 0
      ? null
      : `${count} ${count === 1 ? "title" : "titles"} saved for later`;

  let body;
  if (count === 0) {
    body = (
      <EmptyState
        icon={<BookmarkCheck size={56} />}
        title="Your list is empty"
        text="Save movies and shows you want to watch later and they will wait for you here."
        actionLabel="Find something to watch"
        onAction={onBack}
      />
    );
  } else if (visible.length === 0) {
    body = (
      <EmptyState
        icon={<BookmarkCheck size={56} />}
        title="No matches"
        text="Nothing in your list matches this filter."
        actionLabel="Show all"
        onAction={() => setFilter("all")}
      />
    );
  } else {
    body = (
      <ul className="nq-lib__grid">
        {visible.map((item) => {
          const platforms = splitGenres(item.platform);
          const working = busy === item.title;
          return (
            <li key={item.title} className="nq-card">
              <Poster url={item.posterUrl} type={item.type} />
              <div className="nq-card__body">
                <h3 className="nq-card__title">{item.title}</h3>
                <p className="nq-card__meta">
                  {[item.year, item.type].filter(Boolean).join(" · ")}
                </p>
                <Chips items={splitGenres(item.genre)} />
                {platforms.length > 0 ? (
                  <>
                    <span className="nq-card__label">Available on</span>
                    <Chips items={platforms} platform />
                  </>
                ) : null}
                <div className="nq-card__actions">
                  <button
                    type="button"
                    className="nq-btn nq-btn--primary"
                    disabled={working}
                    onClick={() => run(item, onMarkWatched)}
                  >
                    <Check size={16} aria-hidden="true" />
                    {working ? "Moving to history…" : "Mark watched"}
                  </button>
                  <LikeButton
                    liked={isLiked(item.title)}
                    disabled={working}
                    onClick={() => run(item, onToggleLike)}
                  />
                  <ConfirmRemove
                    label={item.title}
                    onConfirm={() => onRemove(item)}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <LibraryPage
      title="My list"
      subtitle={subtitle}
      onBack={onBack}
      toolbar={
        count > 1 ? <FilterTabs value={filter} onChange={setFilter} /> : null
      }
      toast={toast}
      overlay={overlay}
    >
      {body}
    </LibraryPage>
  );
}
